import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ShoppingBag, Search, Ticket, Sailboat, Crown, Shirt } from "lucide-react";
import { api, thumbUrl } from "@/lib/api";
import { Pagination } from "@/components/Pagination";
import { WishlistHeart } from "@/components/WishlistHeart";
import MarketplaceProduct from "@/pages/MarketplaceProduct";

const CATS = [
  { k: "", l: "All", Icon: ShoppingBag },
  { k: "merch", l: "Merch", Icon: Shirt },
  { k: "tickets", l: "Tickets", Icon: Ticket },
  { k: "activities", l: "Activities", Icon: Sailboat },
  { k: "memberships", l: "Memberships", Icon: Crown },
];
const CAT_KEYS = CATS.map((c) => c.k).filter(Boolean);

export const TYPE_LABELS = {
  physical_merch: "Merch", digital_merch: "Digital", event_ticket: "Event Ticket",
  attraction_ticket: "Attraction Ticket", activity_booking: "Activity", fan_club_membership: "Fan Club",
  venue_membership: "Venue Membership", attraction_membership: "Attraction Membership",
  vip_experience: "VIP Experience", gift_card: "Gift Card", other: "Other",
};

export function ProductCard({ p }) {
  const sale = p.sale_price > 0 && p.sale_price < p.price;
  const shown = Number(p.display_price ?? (sale ? p.sale_price : p.price)) || 0;
  return (
    <Link to={`/marketplace/${p.slug}`} className="group bg-[#111] border border-white/10 rounded-2xl overflow-hidden hover:border-rose-500/50 transition-colors" data-testid={`mkt-card-${p.slug}`}>
      <div className="aspect-square bg-[#0a0a0a] overflow-hidden relative">
        {(p.images?.[0] || p.image_url) ? (
          <img src={thumbUrl(p.images?.[0] || p.image_url)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : <div className="w-full h-full flex items-center justify-center text-zinc-700"><ShoppingBag size={40} /></div>}
        <WishlistHeart productId={p.id} overlay />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-zinc-400">{TYPE_LABELS[p.product_type] || p.product_type}</span>
          {p.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">Featured</span>}
        </div>
        <p className="font-display font-bold leading-snug line-clamp-2">{p.name}</p>
        {p.seller && <p className="text-xs text-zinc-500 mt-0.5">{p.seller.display_name}</p>}
        <div className="mt-2 flex items-center gap-2">
          <span className="font-bold text-rose-400">${shown.toFixed(2)}</span>
          {sale && <span className="text-xs text-zinc-500 line-through">${Number(p.price || 0).toFixed(2)}</span>}
          {p.status === "out_of_stock" && <span className="ml-auto text-xs text-zinc-500">Sold out</span>}
        </div>
      </div>
    </Link>
  );
}

function Storefront({ category }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1 });
  const [q, setQ] = useState(params.get("q") || "");
  const page = Number(params.get("page") || 1);

  useEffect(() => {
    const query = new URLSearchParams({ page, limit: 12 });
    if (category) query.set("category", category);
    if (params.get("q")) query.set("q", params.get("q"));
    api.get(`/marketplace/products?${query}`).then((r) => setData(r.data)).catch(() => {});
  }, [category, page, params]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="marketplace-page">
      <div className="max-w-3xl mb-10">
        <p className="overline text-rose-400">Gulf Coast VIP Marketplace</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">Shop the coast</h1>
        <p className="text-zinc-400 mt-3">Merch, tickets, activities and memberships — sold directly by the artists, venues and attractions you love.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-8">
        {CATS.map(({ k, l, Icon }) => (
          <Link key={k} to={k ? `/marketplace/${k}` : "/marketplace"} data-testid={`mkt-cat-${k || "all"}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${(category || "") === k ? "bg-rose-500 border-rose-500 text-white" : "border-white/15 text-zinc-400 hover:text-white"}`}>
            <Icon size={15} /> {l}
          </Link>
        ))}
        <form className="ml-auto flex items-center gap-2 bg-[#111] border border-white/10 rounded-full px-4 py-2 w-full sm:w-64"
          onSubmit={(e) => { e.preventDefault(); setParams(q ? { q } : {}); }}>
          <Search size={15} className="text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..." className="bg-transparent text-sm outline-none w-full" data-testid="mkt-search-input" />
        </form>
      </div>
      {data.items.length === 0 ? (
        <div className="py-24 text-center text-zinc-500" data-testid="mkt-empty">
          <ShoppingBag className="mx-auto mb-4 text-zinc-700" size={48} />
          No products here yet. Check back soon!
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {data.items.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}
      {data.pages > 1 && <div className="mt-10"><Pagination page={page} pages={data.pages} onPage={(pg) => setParams({ ...(params.get("q") ? { q: params.get("q") } : {}), page: pg })} /></div>}
    </div>
  );
}

export default function Marketplace() {
  const { slug } = useParams();
  if (!slug) return <Storefront category="" />;
  if (CAT_KEYS.includes(slug)) return <Storefront category={slug} />;
  return <MarketplaceProduct slug={slug} />;
}
